import {Injectable} from '@angular/core';
import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import {EquipmentGroup} from './chart-controls/equipment-group/equipment-group.component';
import {ReferenceLine} from './chart-controls/reference-line';
import {SiteInfo} from '@shared/services/api/access-sites.interface';
import {Task} from '@shared/services/api/task.interface';
import {Location} from '@angular/common';
import {Params} from '@angular/router';
import {Chart, ChartAxisXOptions, ChartAxisYOptions, ChartOptions as CanvasJsChartOptions} from 'canvasjs';
import * as CanvasJS from '@app/../assets/libs/canvasjs.min';
import {ObjectUtils} from '@app/shared/util/general/object-utils';
import {JS_CHART_SERIES_TYPE} from "@app/layout/charts/mini-chart/mini-chart.component";
import {DateTimeRangePickerComponent, RelativeDateTimeRange} from "@shared/components/date-time-range-picker/date-time-range-picker.component";
import {CHART_RELATIVE_DATE_PARAM_NAMES} from "@app/layout/charts/chart-url-translator.service";
import {DateSelectComponentFields} from "@shared/components/date-time-range-select/date-time-range-select.component";
import {Equipment, Favourite, MetadataId} from '@app/graphql-typings/graphql';
import {FunctionName} from './chart-functions/chart-function-types';

export enum ConditionOperator {
  EQUALS = '=',
  GREATER_THAN = '>',
  LESS_THAN = '<'
}

export const functionByOperator = new Map<ConditionOperator, (a: number, b: number) => boolean>([
  [ConditionOperator.EQUALS, (a, b) => a === b],
  [ConditionOperator.GREATER_THAN, (a, b) => a > b],
  [ConditionOperator.LESS_THAN, (a, b) => a < b]
]);

export interface ChartOptions extends RelativeDateTimeRange {
  split?: boolean;
  splitByUnit?: boolean;
  splitByEquipment?: boolean;
  chartType?: JS_CHART_SERIES_TYPE;
  workingHours?: boolean;
  timezone?: string;
}

export interface XAxisSelection {
  equipment: Equipment;
  favourite: Favourite;
  binSize: number;
}

export interface MetadataConditionSelection {
  metadataId: MetadataId;
  operator: ConditionOperator;
  conditionValue: number;
}

export interface FilterCondition {
  workingHours: boolean;
  metadataCondition?: MetadataConditionSelection | null;
}

export interface DrawChartEvent extends ChartOptions {
  site: SiteInfo;
  xAxis?: XAxisSelection | null;
  aggregationFunction?: FunctionName | null;
  filterCondition?: FilterCondition | null;
  equipmentGroups: EquipmentGroup[];
  referenceLines?: ReferenceLine[];
}


@Injectable()
export class ChartService {
  private readonly FONT_FAMILY = 'Inter';
  private readonly CUSTOM_THEME_NAME = 'peak-light-1';

  private readonly logger: AceLogger;
  customThemeOptions: CanvasJsChartOptions;


  constructor(private location: Location, logService: AceLoggerService) {
    this.logger = logService.create(ChartService.name);
  }

  getChartLinkQueryParams(task: Task, chartOptions: ChartOptions): Params {
    if (task == null) {
      throw new Error('task cannot be null');
    }
    const favIdsByEquipmentId: Map<number, number[]> = new Map();
    task.sensors.forEach(({ equipment_id, fav_id }) => {
      if (equipment_id) {
        let favIds = favIdsByEquipmentId.get(equipment_id);
        if (favIds == null) {
          favIds = [];
          favIdsByEquipmentId.set(equipment_id, favIds);
        }
        if (fav_id) {
          favIds.push(fav_id);
        }
      }
    });

    const urlParameters = this.getChartLinkQueryParamsForFavIdsByEquipmentIds(favIdsByEquipmentId, chartOptions);
    Object.assign(urlParameters, this.addTaskToURLParams(task.task_id, favIdsByEquipmentId, urlParameters));
    return urlParameters;
  }


  getChartLinkQueryParamsForTaskId(favIdsByEquipmentId: Map<number, number[]>, chartOptions: ChartOptions, taskId?: number): Params {
    const urlParameters = this.getChartLinkQueryParamsForFavIdsByEquipmentIds(favIdsByEquipmentId, chartOptions);
    Object.assign(urlParameters, this.addTaskToURLParams(taskId, favIdsByEquipmentId, urlParameters));
    return urlParameters;
  }

  getChartLinkQueryParamsForFavIdsByEquipmentIds(favIdsByEquipmentIds: Map<number, number[]>, chartOptions: ChartOptions): Params {
    if (!favIdsByEquipmentIds?.size) {
      throw new Error('favIdsByEquipmentIds cannot be empty or null.');
    }
    const urlParameters = this.getChartLinkQueryParamsFromChartOptions(chartOptions);
    favIdsByEquipmentIds.forEach((favIds: number[], equipmentId: number) => {
      if (favIds.length === 0) {
        throw new Error('No favourite is associated with the selected equipment, chart link will be invalid.');
      }
      urlParameters['fav-' + equipmentId] = favIds.join(',');
    });
    return urlParameters;
  }

  buildChart(container: HTMLElement, chartOptions?: CanvasJsChartOptions): Chart {
    if (!chartOptions?.theme || chartOptions?.theme === this.CUSTOM_THEME_NAME) {
      if (!this.customThemeOptions) {
        this.customThemeOptions = this.getDefaultTheme();
        CanvasJS.addTheme(this.CUSTOM_THEME_NAME, this.customThemeOptions);
      }
      // can't spread and recreate chartOptions because some components rely on updating the passed options to update chart state.
      const options: Partial<CanvasJsChartOptions> = chartOptions ?? {};
      options.theme = this.CUSTOM_THEME_NAME;
      return new CanvasJS.Chart(container, options);
    }
    return new CanvasJS.Chart(container, chartOptions);
  }

  private getDefaultTheme(): CanvasJsChartOptions {
    const container = document.createElement('div');
    const chart: Chart = new CanvasJS.Chart(container);
    const themeOptions = (<Chart & {themeOptions: CanvasJsChartOptions}>chart).themeOptions;

    const customThemeOptions: CanvasJsChartOptions = {
      ...themeOptions,
      title: {...themeOptions.title, fontFamily: this.FONT_FAMILY},
      subtitles: themeOptions.subtitles.map(option => ({...option, fontFamily: this.FONT_FAMILY})),
      data: themeOptions.data?.map(option => ({...option, indexLabelFontFamily: this.FONT_FAMILY})),
      axisX: ObjectUtils.mapObjectOrArray(themeOptions.axisX, this.updateAxisOption.bind(this)),
      axisX2: ObjectUtils.mapObjectOrArray(themeOptions.axisX2, this.updateAxisOption.bind(this)),
      axisY: ObjectUtils.mapObjectOrArray(themeOptions.axisY, this.updateAxisYOption.bind(this)),
      axisY2: ObjectUtils.mapObjectOrArray(themeOptions.axisY2, this.updateAxisYOption.bind(this)),
      legend: {...themeOptions.legend, fontFamily: this.FONT_FAMILY},
      toolTip: {...themeOptions.toolTip, fontFamily: this.FONT_FAMILY}
    };
    chart.destroy();
    return customThemeOptions;
  }

  // Some options aren't defined in the "@types/canvasjs" hence the `{crosshair: any}`
  private updateAxisOption<T extends (ChartAxisXOptions | ChartAxisYOptions) & {crosshair?: any}>(axisOption: T): T {
    return {
      ...axisOption,
      labelFontFamily: this.FONT_FAMILY,
      titleFontFamily: this.FONT_FAMILY,
      stripLines: ObjectUtils.mapObjectOrArray(axisOption.stripLines, line => ({...line, labelFontFamily: this.FONT_FAMILY})),
      crosshair: {...axisOption.crosshair, labelFontFamily: this.FONT_FAMILY}
    };
  }

  private updateAxisYOption(axisOption: ChartAxisYOptions): ChartAxisYOptions {
    return {
      ...this.updateAxisOption(axisOption),
      gridThickness: 0.2
    };
  }

  private addTaskToURLParams(taskId, favIdsByEquipmentId: Map<number, number[]>, urlParameters: Params): Params {
    if (taskId != null && favIdsByEquipmentId?.size) {
      const urlParameters: Params = {};
      for (let equipmentId of favIdsByEquipmentId.keys()) {
        urlParameters['task-' + equipmentId] = taskId;
      }
      return urlParameters;
    } else {
      return null;
    }
  }

  private getChartLinkQueryParamsFromChartOptions(chartOptions: ChartOptions): Params {
    const urlParameters = {};
    // dates in chart url should be in UTC timezone
    DateTimeRangePickerComponent.updateQueryParamsForDateTimeRange(urlParameters, CHART_RELATIVE_DATE_PARAM_NAMES, {
      [DateSelectComponentFields.START_DATE]: chartOptions[DateSelectComponentFields.START_DATE]?.clone().utc(),
      [DateSelectComponentFields.END_DATE]: chartOptions[DateSelectComponentFields.END_DATE]?.clone().utc(),
      relativeDate: chartOptions.relativeDate,
      includeToday: chartOptions.includeToday
    });

    if (chartOptions.split != null) {
      urlParameters['split'] = chartOptions.split;
    }

    if (chartOptions.splitByUnit != null) {
      urlParameters['splitbyunit'] = chartOptions.splitByUnit;
    }

    if (chartOptions.splitByEquipment != null) {
      urlParameters['splitbyequip'] = chartOptions.splitByEquipment;
    }

    if (chartOptions.workingHours != null) {
      urlParameters['workinghours'] = chartOptions.workingHours;
    }

    return urlParameters;
  }
}
